export const OPENS_IN_NEW_TAB = 'opens in a new tab';

/**
 * Builds an accessible label by appending ", opens in a new tab" as a suffix.
 * @param accessibleName - The base accessible name for the element.
 */
export function ariaLabelWithNewTab(accessibleName: string): string {
  return `${accessibleName}, ${OPENS_IN_NEW_TAB}`;
}

/**
 * Wait for a specific element within the given root to be rendered and then focus on it.
 * 
 * @param root - The root element or shadow root to search within.
 * @param selector - The CSS selector of the element to wait for.
 */
export async function waitForElementAndFocus(root: Element | ShadowRoot, selector: string) {
  let attempts = 0;
  const maxAttempts = 10;

  // Attempt to find the element within the root
  while (!root.querySelector(selector)) {
    if (attempts >= maxAttempts) {
      console.log('Max attempts reached');
      return;
    }

    // Wait for 500ms before trying again
    await new Promise((r) => setTimeout(r, 500));
    attempts++;
  }

  // On success, focus the element
  (root.querySelector(selector) as HTMLButtonElement)?.focus();
}

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

/**
 * Traps keyboard Tab/Shift+Tab focus within a modal element inside a Shadow DOM.
 * Call this from a keydown listener on the modal element.
 *
 * Needed because Bootstrap's built-in focus trap queries `document` and cannot
 * see elements inside a shadow root. This is particularly relevant when the modal
 * and its trigger button are coupled inside the same component — moving the host
 * element to `document.body` (as `mhecomm-modal` does) is not an option in that
 * case, since it would displace the trigger button from its original DOM position.
 *
 * @param event - The KeyboardEvent from the keydown listener.
 * @param shadowRoot - The shadow root of the host component.
 * @param modalEl - The modal element to trap focus within.
 */
export function handleModalFocusTrap(event: KeyboardEvent, shadowRoot: ShadowRoot, modalEl: HTMLElement) {
  if (event.key !== 'Tab') return;

  const focusable = Array.from(modalEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS));
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = shadowRoot.activeElement;

  if (event.shiftKey ? active === first : active === last) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }
}